import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { AdminMenuProvider } from "@/contexts/AdminMenuContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import NotificationToastProvider from "@/components/NotificationToastProvider";
import Navbar from "@/components/Navbar";
import TopNavbar from "@/components/TopNavbar";
import LeftSideBar from "@/components/LeftSideBar";
import { UpgradeBanner } from "@/components/UpgradeBanner";
import { ClarityInit } from "@/components/ClarityInit";
import Dashboard from "@/pages/Dashboard";
import Authorization from "@/pages/Authorization";
import FreeTrial from "@/pages/FreeTrial";
import CreateAccount from "@/pages/CreateAccount";
import VerifyEmail from "@/pages/VerifyEmail";
import PricingForecast from "@/pages/PricingForecast";
import LandingPage from "@/pages/LandingPage";
import PPDashboard from "@/pages/PPDashboard";
import PSDashboard from "@/pages/PSDashboard";
import PVCDashboard from "@/pages/PVCDashboard";
import PETDashboard from "@/pages/PETDashboard";
import PetChemDashboard from "@/pages/PetChemDashboard";
import "./App.css";
import MarketIntel from "@/components/MarketIntel";
import MonthlySnapshot from "@/pages/MonthlySnapshot";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import { useHubspotTracking } from "@/hooks/useHubspotTracking";
import ErrorBoundary from "@/components/ErrorBoundary";
import Drivers from "@/pages/Drivers";
import News from "@/pages/News";
import DataRefreshStatus from "@/pages/DataRefreshStatus";
import { FeedstockDetails } from "@/components";
import UserManagement from "@/pages/UserManagement";
import EiaForecast from "@/pages/EiaForecast";
import UserActivity from "@/pages/UserActivity";
import RefreshDataPage from "@/pages/RefreshDataPage";
import CommentaryDataPage from "@/pages/CommentaryDataPage";
import DriversPdfDownload from "@/pages/DriversPdfDownload";
import SubscriptionComplete from "@/pages/SubscriptionComplete";
import PetchemDownload from "@/pages/PetchemDownload";
import AccPdfDownload from "@/pages/AccPdfDownload";
import Articles from "@/pages/Articles";
import PEDashboard from "@/pages/PEDashboard";
import PCDashboard from "@/pages/PCDashboard";
import PolicyDashboard from "@/pages/PolicyDashboard";
import FreightRatesDashboard from "@/pages/FreightRatesDashboard";
import DieselDataDashboard from "@/pages/DieselDataDashboard";
import DemoPage from "@/pages/DemoPage";
import { ACCESS_LEVELS } from "@rti/shared";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { isDemoMode } from "@/demo/demoState";
import { trackDemoEvent } from "@/demo/tracking";
import TourController from "@/components/tour/TourController";
import { clearActiveTour, useActiveTour } from "@/components/tour/activeTour";

// The last step of the tour, named by resinsmartOverview.ts — reaching it counts
// as finishing the demo.
const CLOSING_STEP_ID = "closing";

const BillingPage = React.lazy(() => import("@/pages/BillingPage").then(module => ({ default: module.BillingPage })));
const AccountSettings = React.lazy(() => import("@/pages/AccountSettings"));
const CompanySettings = React.lazy(() => import("@/pages/CompanySettings"));
const AdminCompanies = React.lazy(() => import("@/pages/AdminCompanies"));
const AdminParentGroups = React.lazy(() => import("@/pages/AdminParentGroups"));
const EmailManager = React.lazy(() => import("@/pages/EmailManager"));
const HubSpotSync = React.lazy(() => import("@/pages/HubSpotSync"));
const AdminAvailableSubscriptions = React.lazy(() => import("@/pages/AdminAvailableSubscriptions"));
const AdminPromoCodes = React.lazy(() => import("@/pages/AdminPromoCodes"));
const AdminSubscriptions = React.lazy(() => import("@/pages/AdminSubscriptions"));
const AdminFeatureFlags = React.lazy(() => import("@/pages/AdminFeatureFlags"));
const SystemTickets = React.lazy(() => import("@/pages/SystemTickets"));
const AnalyticsDashboard = React.lazy(() => import("@/pages/AnalyticsDashboard"));
const AdminNavItems = React.lazy(() => import("@/pages/AdminNavItems"));
const AdminReports = React.lazy(() => import("@/pages/AdminReports"));
const AdminHub = React.lazy(() => import("@/pages/AdminHub"));
const DemoCapturePage = React.lazy(() => import("@/pages/DemoCapturePage"));
const DemoAnalyticsPage = React.lazy(() => import("@/pages/DemoAnalyticsPage"));
const AdminNotificationTest = React.lazy(() => import("@/pages/AdminNotificationTest"));
const SpendOverview = React.lazy(() => import("./pages/SpendOverview"));
const ResinOverview = React.lazy(() => import("./pages/ResinOverview"));

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isTrialExpired, isSubscriptionLapsed } = useAuth();
  const { getFlag, isLoading: isFeatureFlagsLoading } = useFeatureFlagsContext();
  const billingEnabled = !isFeatureFlagsLoading && getFlag('enable_billing');
  const accessLocked = isAuthenticated && (isTrialExpired || isSubscriptionLapsed);
  const showLeftNav = isAuthenticated && !isFeatureFlagsLoading && getFlag("left_nav") && !accessLocked;
  const activeTour = useActiveTour();
  useActivityTracker();

  const hsUser = {
    email: user?.email,
    first_name: user?.first_name,
    last_name: user?.last_name,
  }
  useHubspotTracking(hsUser);

  React.useEffect(() => {
    const hubspotTrackingEnabled =
      ((import.meta as any).env?.VITE_HUBSPOT_TRACKING_ENABLED as string | undefined) === "true";

    if (!hubspotTrackingEnabled) return;
    // Public demo visitors must not land in HubSpot contact tracking.
    if (isDemoMode()) return;
    if (window.location.pathname === '/free-trial' || window.location.pathname === '/create-account') return;
    if (document.getElementById('hs-script-loader')) return;

    const script = document.createElement('script');
    script.id = 'hs-script-loader';
    script.src = '//js.hs-scripts.com/48955579.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const showNavbar = false;

  // Kept stable so the tour doesn't re-run its steps on every App render.
  const handleExit = React.useCallback(() => {
    if (isDemoMode()) trackDemoEvent('tour_exited');
    clearActiveTour();
  }, []);

  const handleStepView = React.useCallback((stepId: string) => {
    trackDemoEvent('step_viewed', { stepId });
    if (stepId === CLOSING_STEP_ID) trackDemoEvent('tour_completed');
  }, []);

  const handleCta = React.useCallback((label: string) => {
    trackDemoEvent('cta_clicked', { cta: label });
  }, []);

  return (
    <div className="App h-screen overflow-hidden flex flex-col">
      <ClarityInit />
      {activeTour && (
        <TourController
          tour={activeTour}
          onComplete={clearActiveTour}
          onExit={handleExit}
          onStepView={isDemoMode() ? handleStepView : undefined}
          onCta={isDemoMode() ? handleCta : undefined}
        />
      )}
      {showNavbar && <Navbar />}
      {isAuthenticated && <ImpersonationBanner />}
      {isAuthenticated && <TopNavbar />}
      <main className={`${showLeftNav ? "flex" : ""} flex-1 min-h-0 overflow-hidden`}>
        {showLeftNav && <LeftSideBar />}
        {/* data-app-scroll: the page's scroll box, not the window. Fixed overlays find it here. */}
        <div data-app-scroll className={`${showLeftNav ? "flex-1 min-w-0" : "w-full"} h-full overflow-y-auto`}>
          {isAuthenticated && billingEnabled && <UpgradeBanner />}
          <ErrorBoundary>
            <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/authorization" element={<Authorization />} />
            <Route path="/free-trial" element={<FreeTrial />} />
            <Route path="/create-account" element={<CreateAccount />} />
            <Route path="/reset-password" element={<Authorization />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/drivers/download" element={<DriversDownloadRedirect />} />
            <Route path="/download/petchem" element={<PetchemDownload />} />
            <Route path="/demo/embed/:slug" element={<DemoPage isEmbed />} />
            <Route path="/demo/:slug" element={<DemoPage />} />

            {/* Protected routes - authentication required */}
            <Route element={<ProtectedRoute />}>
              <Route path="/download/drivers" element={<DriversPdfDownload />} />
              <Route path="/download/acc" element={<AccPdfDownload />} />
              <Route path="/account/subscription/complete" element={<SubscriptionComplete />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/snapshot/:pathId" element={<MonthlySnapshot />} />
              <Route path="/acc/:pathId" element={<AccToSnapshotsRedirect />} />
              <Route path="/marketIntel/pe-dashboard" element={<Navigate to="/marketIntel/pe" replace />} />
              <Route path="/marketIntel/pp-dashboard" element={<Navigate to="/marketIntel/pp" replace />} />
              <Route path="/marketIntel/ps-dashboard" element={<Navigate to="/marketIntel/ps" replace />} />
              <Route path="/marketIntel/pvc-dashboard" element={<Navigate to="/marketIntel/pvc" replace />} />
              <Route path="/marketIntel/pet-dashboard" element={<Navigate to="/marketIntel/pet" replace />} />
              <Route path="/marketIntel/petchem-dashboard" element={<Navigate to="/marketIntel/petchem" replace />} />
              <Route path="/marketIntel/pe" element={<PEDashboard />} />
              <Route path="/marketIntel/pp" element={<PPDashboard />} />
              <Route path="/marketIntel/ps" element={<PSDashboard />} />
              <Route path="/marketIntel/pvc" element={<PVCDashboard />} />
              <Route path="/marketIntel/pet" element={<PETDashboard />} />
              <Route path="/marketIntel/pc" element={<PetChemDashboard />} />
              <Route path="/marketIntel/petchem" element={<PetChemDashboard />} />
              <Route path="/marketIntel/polycarbonate" element={<PCDashboard />} />
              <Route
                path="/marketIntel/:reportID"
                element={<MarketIntel />}
              />
              <Route path="/eia-forecast" element={<EiaForecast />} />
              <Route path="/forecast" element={
                <CompoundLevelRoute>
                  <PricingForecast />
                </CompoundLevelRoute>
              } />
              <Route path="/forecast/:type" element={
                <CompoundLevelRoute>
                  <PricingForecast />
                </CompoundLevelRoute>
              } />
              <Route path="/drivers" element={<Drivers />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/news" element={<News />} />
              <Route path="/feedstock-details" element={<FeedstockDetails />} />
              <Route path="/landing-page" element={
                <LandingPageFlagRoute>
                  <LandingPage />
                </LandingPageFlagRoute>
              } />
              <Route path="/spend-overview" element={
                <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                  <SpendOverview />
                </React.Suspense>
              } />
              <Route path="/resin-overview" element={
                <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                  <ResinOverview />
                </React.Suspense>
              } />
              <Route path="/market-pulse" element={<Navigate to="/landing-page" replace />} />
              <Route
                path="/account"
                element={
                  <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                    <AccountSettings />
                  </React.Suspense>
                }
              />
              <Route path="/policy-dashboard" element={
                <PolicyDashboardFlagRoute>
                  <PolicyDashboard />
                </PolicyDashboardFlagRoute>
              } />
              <Route path="/freight-rates" element={<FreightRatesDashboard />} />
              <Route path="/diesel-data" element={<DieselDataDashboard />} />
              <Route
                path="/billing"
                element={
                  <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                    <BillingPage />
                  </React.Suspense>
                }
              />

              <Route
                path="/company-settings"
                element={
                  <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                    <CompanySettings />
                  </React.Suspense>
                }
              />

              {/* Admin-only routes */}
              <Route element={<ProtectedRoute requireAdmin={true} />}>
                <Route path="/policy-dashboard" element={
                  <PolicyDashboardFlagRoute>
                    <PolicyDashboard />
                  </PolicyDashboardFlagRoute>
                } />
                <Route path="/freight-rates" element={<FreightRatesDashboard />} />
                <Route path="/diesel-data" element={<DieselDataDashboard />} />
                <Route path="/refresh-data" element={<RefreshDataPage />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/user-activity/:email" element={<UserActivity />} />
                <Route path="/data-refresh-status" element={<DataRefreshStatus />} />
                <Route path="/commentary-data" element={<CommentaryDataPage />} />
                <Route
                  path="/system/companies"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminCompanies />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/parent-groups"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminParentGroups />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/email-manager"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <EmailManager />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/hubspot-sync"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <HubSpotSync />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/available-subscriptions"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminAvailableSubscriptions />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/promo-codes"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminPromoCodes />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/notification-test"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminNotificationTest />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/feature-flags"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminFeatureFlags />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/nav-items"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminNavItems />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/reports"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminReports />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/demo-capture"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <DemoCapturePage />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/demo-analytics"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <DemoAnalyticsPage />
                    </React.Suspense>
                  }
                />
              </Route>

              {/* Employee + Admin only — both required together, neither alone is enough */}
              <Route element={<ProtectedRoute requireAdmin={true} requireEmployee={true} />}>
                <Route
                  path="/system/subscriptions"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AdminSubscriptions />
                    </React.Suspense>
                  }
                />
              </Route>

              <Route element={<ProtectedRoute requireEmployee={true} />}>
                <Route path="/system" element={
                  <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                    <AdminHub />
                  </React.Suspense>
                } />
                <Route
                  path="/system/analytics"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <AnalyticsDashboard />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/system/tickets"
                  element={
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-rs-off-white">Loading...</div></div>}>
                      <SystemTickets />
                    </React.Suspense>
                  }
                />
              </Route>
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ErrorBoundary>
          <div
            style={{
              position: "fixed",
              top: "1rem",
              right: "1rem",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              gap: ".5rem",
            }}
          >
          </div>
        </div>
      </main>
    </div>
  );
};

const DriversDownloadRedirect: React.FC = () => {
  const { search } = window.location;
  return <Navigate to={`/download/drivers${search}`} replace />;
};

const AccToSnapshotsRedirect: React.FC = () => {
  const { pathId } = useParams<{ pathId: string }>();
  return <Navigate to={`/snapshot/${pathId || ''}`} replace />;
};

const CompoundLevelRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const hasCompoundLevel = isAuthenticated && user && user.effective_membership_id && user.effective_membership_id >= ACCESS_LEVELS.COMPOUND;
  if (isLoading) return null;
  if (!hasCompoundLevel) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const PolicyDashboardFlagRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getFlag, isLoading } = useFeatureFlagsContext();
  if (isLoading) return null;
  if (!getFlag("policy_dashboard")) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const LandingPageFlagRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getFlag, isLoading } = useFeatureFlagsContext();
  if (isLoading) return null;
  if (!getFlag("landing_page")) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <NotificationToastProvider>
        <AuthProvider>
          <DataProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <FeatureFlagsProvider>
                <AdminMenuProvider>
                  <AppContent />
                </AdminMenuProvider>
              </FeatureFlagsProvider>
            </Router>
          </DataProvider>
        </AuthProvider>
      </NotificationToastProvider>
    </ThemeProvider>
  );
}

export default App;
